// Global accessibility settings for the Aiga Enable website.
// Settings are a plain serialisable object applied to <html> via classes, so a
// future A-ID (Accessibility ID) profile can apply a person's saved website
// preferences without rebuilding this system:
//   window.dispatchEvent(new CustomEvent("a11y:apply", { detail: aidPrefs }))

const STORAGE_KEY = "aiga-a11y-settings";

// Root font-size percentages. Index 2 = 100% (site default).
export const TEXT_SIZE_STEPS = [87.5, 93.75, 100, 112.5, 125, 137.5, 150];
export const DEFAULT_TEXT_STEP = 2;

export const DEFAULT_SETTINGS = {
  textStep: DEFAULT_TEXT_STEP,
  highContrast: false,
  darkMode: true, // the site's default theme is dark
  reduceMotion: false,
  highlightLinks: false,
  strongFocus: false,
  largeCursor: false,
  lineSpacing: false,
  letterSpacing: false,
};

const CLASS_BY_KEY = {
  highContrast: "a11y-contrast",
  reduceMotion: "a11y-motion",
  highlightLinks: "a11y-links",
  strongFocus: "a11y-focus",
  largeCursor: "a11y-cursor",
  lineSpacing: "a11y-line",
  letterSpacing: "a11y-letter",
};

export function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    /* fall through to defaults */
  }
  const init = { ...DEFAULT_SETTINGS };
  // Respect the operating-system reduced-motion preference on first visit.
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    init.reduceMotion = true;
  }
  return init;
}

export function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* storage unavailable — settings simply won't persist */
  }
}

export function clearSettings() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function applySettings(settings) {
  const root = document.documentElement;
  root.style.fontSize =
    settings.textStep === DEFAULT_TEXT_STEP
      ? ""
      : `${TEXT_SIZE_STEPS[settings.textStep]}%`;
  for (const [key, cls] of Object.entries(CLASS_BY_KEY)) {
    root.classList.toggle(cls, Boolean(settings[key]));
  }
  root.classList.toggle("a11y-light", !settings.darkMode);
}
